import { createBlockInteractionsModification } from '@carboncopyco/editor-utils';
import type { Modification } from '@carboncopyco/types';
import type { NavigationEditSchemaType } from '@carboncopyco/types';
import { TextMenuHeader } from '@carboncopyco/ui';
import { useMemo } from 'react';
import { BlockInteractionForm } from '../NavigationRuleForm';
import {
  RuleCardDropdown,
  type RuleCardDropdownProps,
  RulesContent,
} from '../RulesManager';
import { NavigationRulePreview } from './NavigationRulePreview';

export interface NavigationRuleListItemProps {
  rule: Modification<NavigationEditSchemaType.blockInteractions>;
  onDelete: (
    rule: Modification<NavigationEditSchemaType.blockInteractions>
  ) => void;
  open: boolean;
  onOpenRule: () => void;
  onCloseRule: () => void;
  onEdit: (
    rule: Modification<NavigationEditSchemaType.blockInteractions>
  ) => Promise<unknown>;
}

export const NavigationRuleListItem = ({
  rule,
  open,
  onCloseRule,
  onOpenRule,
  onDelete,
  onEdit,
}: NavigationRuleListItemProps) => {
  const { elementData = {}, blockingType } = rule.content;
  const dropdownItems = useMemo(
    () =>
      [
        {
          type: 'edit',
          onClick: () => {
            onOpenRule();
          },
        },
        {
          type: 'delete',
          onClick: () => onDelete(rule),
        },
      ] as RuleCardDropdownProps['items'],
    [rule, onDelete, onOpenRule]
  );

  const onSubmit = async ({ blockAcross, blockBy }) => {
    const updateRule = createBlockInteractionsModification({
      blockAcross,
      blockBy,
      elementData,
      blockingType,
      pageUrl: String(elementData.pageUrl),
    });

    await onEdit({
      ...rule,
      selectors: updateRule.selectors,
      content: updateRule.content,
    });

    setTimeout(onCloseRule, 300);
  };

  return (
    <RulesContent.List.Item
      id={rule.id}
      header={
        open ? (
          <TextMenuHeader
            label="Rule settings"
            color="text-ds-natural-600"
          />
        ) : (
          <NavigationRulePreview rule={rule} />
        )
      }
      content={
        <BlockInteractionForm
          linkEditRule={rule}
          currentPageUrl={String(elementData.pageUrl) || ''}
          elementMetadata={elementData}
          onCancel={onCloseRule}
          onSubmit={onSubmit}
          elementType={blockingType}
        />
      }
      action={<RuleCardDropdown items={dropdownItems} />}
    />
  );
};
